<template lang="pug">
.insights-timeline-widget.insights-widget
  .widget-wrapper
    .widget-common.widget-full-height
      .widget-title.mb-0
        .insights-timeline-widget-header.flex-column.w-full
          .d-flex.justify-content-between
            div {{ title }}
            div
              a.d-flex.align-items-center.gap-5.text-transform-none(
                v-tooltip
                v-track:click="{ label: 'Timeline View new timeline page Clicked', widget_name: 'Insights Timeline Widget' }"
                title="View activity level, manual, idle, breaks, shifts, PTO, and holidays"
                :href="timelineUrl"
                target="_blank"
                rel="noopener noreferrer"
              )
                i.hi.hi-external
                | View new timeline page

          .insights-timeline-widget-header-content.flex-wrap(v-if="isReady && !isEmptyWidget")
            div
              label Hours
              .total.m-0
                .mr-5 Total:
                span {{ $filters.formatDuration(totalTracked) }}
            InsightsTimelineFilter

      .widget-body(:class="bodyClasses")
        template(v-if="isReady")
          .d-flex.flex-column(v-if="!isEmptyWidget")
            .insights-timeline-row(
              v-for="(row, i) in rows"
              :key="`${i}-${remoteVsOffice}`"
            )
              .insights-timeline-row-header.justify-content-between.w-full.flex-wrap.gap-5
                .d-flex.align-items-center
                  .date {{ format(row.date) }}
                  .tracked.d-flex.align-items-center
                    i.hi.hi-time
                    span.ml-5
                      | {{ $filters.formatDuration(row.tracked, false) }}
                .averages.d-flex.flex-wrap
                  .timeline-average.average-core-work
                    | AVG. CORE WORK:
                    span.value.ml-5 {{ formatAverage(row.proficiencyScore) }}
                  .timeline-average.average-activity
                    | AVG. ACTIVITY:
                    span.value.ml-5 {{ formatAverage(row.activity * 100) }}

              AppTimelineGraph(
                :key="`${i}-${remoteVsOffice}`"
                v-track:click="trackDetailsProperties"
                :date="row.date"
                :values="row.timelineValues"
                :show-now="false"
                :show-location-lines="remoteVsOffice"
                @value-click="openPopover"
              )
            AppPopover(
              v-if="canShowDetails"
              ref="appPopover"
              placement="top"
              extra-popover-classes="insights-timeline-popover"
              lazy
              :reference="reference"
              @hide="clearPopovers"
            )
              template(#body)
                InsightsTimelineDetails(:details="details")
          InsightsNoData(
            v-else
            :type="emptyReason"
          )
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { INSIGHTS } from '~/app_constants';
import { get, kebabCase } from 'lodash-es';
import { parseInternalDate, parseInternalDatetime } from '~/utils/time_utils';
import { keysToCamelCase } from '~/utils/object_utils';
import { formatDate } from '~/utils/time_utils.js';

import { Insights, InsightsTracking } from '~/mixins';
import { mapGetters } from 'vuex';
import AppPopover from '~/components/AppPopover.vue';
import InsightsTimelineDetails from './InsightsTimelineDetails.vue';
import InsightsTimelineFilter from './InsightsTimelineFilter.vue';
import AppTimelineGraph from '~/components/AppTimelineGraph.vue';

export default {
  name: 'InsightsTimelineWidget',
  components: {
    AppPopover,
    AppTimelineGraph,
    InsightsTimelineDetails,
    InsightsTimelineFilter,
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [Insights, InsightsTracking],
  data: () => ({
    details: null,
    reference: null,
    widgetKey: INSIGHTS.api.timeline,
    title: 'Timeline',
  }),
  computed: {
    ...mapGetters(['remoteVsOffice']),
    ...mapGetters({
      widgetData: 'timeline',
    }),
    totalTracked() {
      return this.widgetData ? this.widgetData.tracked : null;
    },
    rows() {
      const rows = get(this.widgetData, 'rows', []).filter((row) => !!row.tracked);

      return rows.map((row) => ({
        date: parseInternalDate(row.date),
        tracked: row.tracked,
        proficiencyScore: row.proficiency_score,
        activity: row.avg_activity,
        timelineValues: row.blocks.map((block) => ({
          start: parseInternalDatetime(block.time_from),
          stop: parseInternalDatetime(block.time_to),
          cssClass: `clickable proficiency-${kebabCase(block.type)}`,
          meta: {
            proficiencyScore: block.proficiency_score,
            activity: block.activity,
            status: block.status,
            locationType: block.location_type,
            projects: block.projects.map((project) => keysToCamelCase(project)),
            tools: block.tools.map((tool) => keysToCamelCase(tool)),
            toolsHidden: block.tools_hidden,
          },
        })),
      }));
    },
    canShowDetails() {
      return !!this.details && !!this.reference;
    },
    timelineUrl() {
      const params = new URLSearchParams();

      params.set('tab', 'timeline');
      params.set('user_id', this.filters.user_id);
      params.set('from', this.filters.date.from);
      params.set('to', this.filters.date.to);
      params.set('time_zone', this.filters.time_zone);

      return `?${params.toString()}`;
    },
    trackDetailsProperties() {
      return Object.freeze({
        label: 'Widget click',
        widget_name: this.title,
        action_type: 'details',
        action_label: 'Timeline graph',
        ...this.commonProps(),
      });
    },
  },
  methods: {
    async openPopover({ target, value }) {
      await this.clearPopovers();


      this.details = value;
      this.reference = target;

      await this.$nextTick();

      this.$refs.appPopover.open();
    },
    async clearPopovers() {
      this.details = null;
      this.reference = null;

      await this.$nextTick();
    },
    format(date) {
      return formatDate(date);
    },
    formatAverage(value) {
      return value !== null ? `${Math.round(value * 10) / 10}%` : '-';
    },
  },
};
</script>
